import React, { useState } from 'react';
import { AlertCircle, Calendar, Phone, User, Mail, UserX } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import {
  PatientDetails,
  setPatientDetails,
  setPatientType,
  createEmptyPatientDetails,
} from '../../redux/slices/appointmentSlice';
import { Button } from '../Button';
import { PatientSearchResponse, search_by_phone_birthdate } from '../../services/api';
import { PATIENT_TYPE_TO_BACKEND, PatientTypeEnum } from '../../types';
import { formatPhoneInput, PHONE_REGEX, MIN_DOB, getYesterday } from '../../utils/helpers';

type Field = keyof PatientDetails;

export const ExistingPatientForm: React.FC = () => {
  const dispatch = useDispatch();
  const patientDetails = useSelector((state: RootState) => state.appointment.patientDetails);
  const { practice } = useSelector((s: RootState) => s.auth);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchResult, setSearchResult] = useState<PatientSearchResponse['data'] | null>(null);
  const [notFound, setNotFound] = useState(false);

  const canContinue = Boolean(
    !searching &&
    PHONE_REGEX.test(patientDetails.phone) &&
    patientDetails.birth_date &&
    patientDetails.birth_date <= getYesterday() &&
    patientDetails.birth_date >= MIN_DOB
  );

  const update = (field: Field, value: string) => {
    dispatch(setPatientDetails({ ...patientDetails, [field]: value }));
  };

  const handlePhoneChange = (value: string) => {
    update('phone', formatPhoneInput(value));
  };

  const handleSwitchToNew = () => {
    dispatch(setPatientType(PatientTypeEnum.New));
    dispatch(setPatientDetails(createEmptyPatientDetails()));
    setNotFound(false);
    setSearchResult(null);
    setSearchError(null);
  };

  const handleSearch = async () => {

    setSearching(true);
    setSearchError(null);
    setNotFound(false);
    setSearchResult(null);

    try {
      const response = await search_by_phone_birthdate({
        phone_number: patientDetails.phone,
        birth_date: patientDetails.birth_date || '',
        tenant_id: practice?.tenant_id || ''
      });

      const attributes = response?.data?.attributes || {};
      setSearchResult(response.data);

      dispatch(
        setPatientDetails({
          ...patientDetails,
          firstName: attributes.first_name || '',
          lastName: attributes.last_name || '',
          email: attributes.email || '',
          phone: attributes.mobile_phone ? formatPhoneInput(attributes.mobile_phone) : patientDetails.phone,
          birth_date: attributes.birth_date || patientDetails.birth_date,
          id: response.data.id,
          external_resource_id: attributes.external_resource_id,
        })
      );
    } catch (error: any) {
      const status = error?.response?.data?.status || error?.response?.status;
      if (status === 'not_found' || status === 404) {
        setNotFound(true);
      } else {
        setSearchError('Unable to find patient. Please try again.');
      }
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="form-control">
          <label className="label">
            <span className="label-text">
              Phone Number <span className="text-error">*</span>
            </span>
          </label>
          <div className="form-control">
            <label className={`input input-bordered flex items-center gap-2 bg-white text-black ${patientDetails.phone && !PHONE_REGEX.test(patientDetails.phone) ? 'border-error focus-within:border-error focus-within:ring-error' : 'border-teamcare-blue focus-within:border-teamcare-blue focus-within:ring-teamcare-blue'}`}>
              <Phone size={18} className="text-gray-400" />
              <input
                type="text"
                className="grow bg-transparent text-black placeholder-gray-400"
                placeholder="(XXX)XXX-XXXX"
                value={patientDetails.phone}
                onChange={(e) => handlePhoneChange(e.target.value)}
                maxLength={13}
                inputMode="numeric"
              />
            </label>
            {patientDetails.phone && !PHONE_REGEX.test(patientDetails.phone) && (
              <label className="label">
                <span className="label-text-alt text-error">Phone number must be 10 digits</span>
              </label>
            )}
          </div>
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text">
              Date of Birth <span className="text-error">*</span>
            </span>
          </label>
          <label className={`input input-bordered flex items-center gap-2 bg-white text-black ${patientDetails.birth_date && (patientDetails.birth_date > getYesterday() || patientDetails.birth_date < MIN_DOB) ? 'border-error focus-within:border-error focus-within:ring-error' : 'border-teamcare-blue focus-within:border-teamcare-blue focus-within:ring-teamcare-blue'}`}>
            <Calendar size={18} className="text-gray-400" />
            <input
              type="date"
              className="grow bg-transparent text-black placeholder-gray-400"
              value={patientDetails.birth_date || ''}
              max={getYesterday()}
              min={MIN_DOB}
              onChange={(e) => update('birth_date', e.target.value)}
            />
          </label>
          {patientDetails.birth_date && (patientDetails.birth_date > getYesterday() || patientDetails.birth_date < MIN_DOB) && (
            <label className="label">
              <span className="label-text-alt text-error">Date of birth must be between {MIN_DOB} and yesterday</span>
            </label>
          )}
        </div>
      </div>

      <Button
        title={searching ? 'Searching...' : 'Search'}
        onClick={handleSearch}
        disabled={!canContinue}
        className="w-full md:w-auto"
      />

      {searchError && (
        <div className="flex items-center text-sm text-red-600 gap-2">
          <AlertCircle size={16} />
          <span>{searchError}</span>
        </div>
      )}

      {searchResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-center mb-3">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
              <User className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-green-800 text-base">Patient Found!</h3>
              <p className="text-green-600 text-sm">Welcome back, {patientDetails.firstName}!</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-green-700 font-medium">Name:</span>
              <span className="ml-2 text-green-800">{patientDetails.firstName} {patientDetails.lastName}</span>
            </div>
            <div>
              <span className="text-green-700 font-medium">Email:</span>
              <span className="ml-2 text-green-800">{patientDetails.email}</span>
            </div>
            <div>
              <span className="text-green-700 font-medium">Phone:</span>
              <span className="ml-2 text-green-800">{patientDetails.phone}</span>
            </div>
          </div>
        </div>
      )}

      {notFound && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center mb-3">
            <AlertCircle className="w-6 h-6 text-red-500 mr-3" />
            <div>
              <h3 className="font-bold text-red-800 text-base">Patient Not Found</h3>
              <p className="text-red-600 text-sm">No record found with this information</p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-3 border border-red-200">
            <p className="text-red-700 text-sm mb-3">
              We couldn't find a patient record with the provided information.
              This might be your first visit to our practice.
            </p>
            <button
              onClick={handleSwitchToNew}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all duration-300 text-sm font-medium"
            >
              <UserX className="w-4 h-4 mr-2" />
              Register as New Patient
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

